/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";

type Props = {
  icon: string;
  title: string;
  subtitle: string;
  done: number;
  total: number;
  /** Signed off / all done. */
  complete: boolean;
  issues?: number | null;
  /** "13:00" — shown, and flagged late once passed while not complete. */
  dueTime?: string | null;
  isToday: boolean;
  onOpen: () => void;
};

const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// A procedure or prep list due today, with its progress.
export const DueCard = defineComponent<Props>({
  name: "DueCard",
  props: ["icon", "title", "subtitle", "done", "total", "complete", "issues", "dueTime", "isToday", "onOpen"],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      const late = props.isToday && !props.complete && props.dueTime != null && props.dueTime < nowHHMM();
      const pct = props.total ? Math.round((props.done / props.total) * 100) : 0;
      return (
        <button
          class={[
            "flex w-full flex-col gap-3 rounded-xl border bg-base-100 p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow",
            props.complete ? "border-success/40" : late ? "border-error/50" : "border-base-300",
          ].join(" ")}
          onClick={() => props.onOpen()}
        >
          <div class="flex items-start gap-3">
            <span
              class={[
                "material-symbols-outlined rounded-lg p-2",
                props.complete ? "bg-success/15 text-success" : late ? "bg-error/10 text-error" : "bg-primary/10 text-primary",
              ].join(" ")}
            >
              {props.complete ? "task_alt" : props.icon}
            </span>
            <div class="min-w-0 flex-1">
              <div class="font-semibold leading-tight">{props.title}</div>
              <div class="mt-0.5 text-xs text-base-content/60">{props.subtitle}</div>
            </div>
            {props.dueTime && (
              <span class={["badge badge-sm whitespace-nowrap", late ? "badge-error" : "badge-ghost"].join(" ")}>
                {late ? t("workday.myday.late") : t("workday.myday.by")} {props.dueTime}
              </span>
            )}
          </div>
          <div class="flex items-center gap-2">
            <progress class={["progress h-1.5 flex-1", props.complete ? "progress-success" : "progress-primary"].join(" ")} value={pct} max="100" />
            <span class="text-xs tabular-nums text-base-content/60">{`${props.done}/${props.total}`}</span>
          </div>
          {props.issues ? (
            <span class="inline-flex items-center gap-1 text-xs font-medium text-error">
              <span class="material-symbols-outlined text-sm">warning</span>
              {t("workday.common.issues").replace("{count}", String(props.issues))}
            </span>
          ) : null}
        </button>
      );
    };
  },
});
